import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Clock,
  Copy,
  Edit,
  FileText,
  Filter,
  Globe,
  MessageSquare,
  Plus,
  Search,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCoachingTemplates, CoachingTemplate } from '@/hooks/useCoachingTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const categoryLabels: Record<string, { label: string; icon: typeof BarChart3; color: string }> = {
  sales: { label: 'Sales', icon: BarChart3, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100' },
  strategy: { label: 'Strategy', icon: Target, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-100' },
  people: { label: 'People', icon: Users, color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-100' },
  communication: { label: 'Communication', icon: MessageSquare, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-100' },
  education: { label: 'Education / Professional Development', icon: BookOpen, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-100' },
};

function normaliseTemplateCategory(category: string): string {
  return category === 'professional_development' || category === 'professional_dev' ? 'education' : category;
}

export default function CoachingTemplates() {
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const queryClient = useQueryClient();
  const { data: templates = [], isLoading } = useCoachingTemplates();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; template: CoachingTemplate | null }>({ open: false, template: null });

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (template.description?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || normaliseTemplateCategory(template.category) === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const stats = Object.keys(categoryLabels).reduce<Record<string, number>>((acc, key) => {
    acc[key] = templates.filter(t => normaliseTemplateCategory(t.category) === key).length;
    return acc;
  }, {});

  const handleDelete = async () => {
    if (deleteModal.template) {
      const { error } = await supabase
        .from('coaching_templates')
        .delete()
        .eq('id', deleteModal.template.id);

      if (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete template: ' + error.message);
      } else {
        toast.success('Template deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['coaching-templates'] });
      }
      setDeleteModal({ open: false, template: null });
    }
  };

  const handleClone = async (template: CoachingTemplate) => {
    try {
      // Get user's company_id
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) {
        toast.error('You must be logged in to clone templates');
        return;
      }

      // Use user.company_id (tenant-scoped via tenant_memberships)
      if (!user?.company_id) {
        toast.error('You must be assigned to a company in this tenant to clone templates');
        return;
      }

      // Clone the template with the current-tenant company_id
      const { error } = await supabase
        .from('coaching_templates')
        .insert({
          name: `${template.name} (Copy)`,
          description: template.description,
          category: template.category,
          template_type: template.template_type,
          icon: template.icon,
          estimated_duration: template.estimated_duration,
          output_document: template.output_document,
          is_default: false,
          is_active: true,
          intake_script: template.intake_script,
          document_template: template.document_template,
          scoring_criteria: template.scoring_criteria,
          company_id: user.company_id,
          parent_template_id: template.id
        });

      if (error) {
        toast.error('Failed to clone template');
        console.error('Clone error:', error);
      } else {
        toast.success('Template cloned to your company');
        queryClient.invalidateQueries({ queryKey: ['coaching-templates'] });
      }
    } catch (err) {
      console.error('Clone error:', err);
      toast.error('Failed to clone template');
    }
  };

  // Check if user can edit a template (admin or company admin for their templates)
  const canEditTemplate = (template: CoachingTemplate) => {
    if (hasRole('admin')) return true;
    // Company admins can only edit templates assigned to their company
    // This is handled by RLS, but we hide the button if they can't edit
    return false; // RLS will handle actual permission
  };

  // Check if user can clone a template (company admin for templates not in their company)
  const canCloneTemplate = (template: CoachingTemplate) => {
    return hasRole('company_admin') && !hasRole('admin');
  };

  return (
    <div className="medd-page">
      <div className="medd-page-inner space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="medd-page-title">Output Templates</h1>
            <p className="medd-page-subtitle">
              Manage coaching document templates, outputs, durations, and reusable conversation structures.
            </p>
          </div>
          {hasRole('admin') && (
            <Button onClick={() => navigate('/admin/coaching-templates/create')} className="w-full sm:w-auto sm:shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          )}
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
          {Object.entries(categoryLabels).map(([key, { label, icon: Icon, color }]) => (
            <Card
              key={key}
              className={`medd-stat-card cursor-pointer ${categoryFilter === key ? 'border-primary bg-primary/5' : ''}`}
              onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="medd-icon-tile bg-muted text-primary">
                    <Icon size={18} />
                  </div>
                  <span className="font-semibold">{label}</span>
                </div>
                <Badge variant="secondary" className={color}>
                  {stats[key] || 0}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="medd-command-panel flex flex-col gap-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(categoryLabels).map(([key, { label, icon }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader><div className="h-6 bg-muted rounded w-3/4" /></CardHeader>
                <CardContent><div className="h-20 bg-muted rounded" /></CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredTemplates.map(template => {
              const category = categoryLabels[normaliseTemplateCategory(template.category)] || categoryLabels.sales;
              const CategoryIcon = category.icon;
              
              return (
                <Card key={template.id} className="hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg transition-all">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {template.description || 'No description'}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${category.color} gap-1`}>
                        <CategoryIcon className="h-3 w-3" />
                        {category.label}
                      </Badge>
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {template.estimated_duration} min
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="w-3 h-3" />
                      Output: {template.output_document}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Globe className="w-3 h-3" /> All users
                    </div>

                    {/* Admin actions */}
                    {hasRole('admin') && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/admin/coaching-templates/edit/${template.id}`)}
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setDeleteModal({ open: true, template })}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}

                    {/* Company admin clone action */}
                    {canCloneTemplate(template) && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleClone(template)}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Clone to My Company
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {filteredTemplates.length === 0 && !isLoading && (
          <Card className="medd-empty-state">
            <p className="text-muted-foreground">No templates found matching your criteria</p>
          </Card>
        )}

        {deleteModal.template && (
          <DeleteConfirmModal
            open={deleteModal.open}
            agentName={deleteModal.template.name}
            onConfirm={handleDelete}
            onCancel={() => setDeleteModal({ open: false, template: null })}
          />
        )}
      </div>
    </div>
  );
}
